import * as React from 'react';

import {
  Badge,
  BadgeProps,
  Box,
  BoxProps,
  ButtonGroup,
  CardBody,
  Card as ChakraCard,
  CardFooter as ChakraCardFooter,
  CardHeader as ChakraCardHeader,
  CardProps as ChakraCardProps,
  Flex,
  Heading,
  HeadingProps,
  HStack,
  Image,
  ImageProps,
  Text,
  TextProps,
  ThemingProps,
  VStack,
} from '@chakra-ui/react';

import { cn } from '@/lib/utils';

import { Avatar, AvatarProps } from './Avatar';
import { Button } from './Button';

// Card Variants
export type CardVariant = 'elevated' | 'outline' | 'filled' | 'unstyled';

// Card Sizes
export type CardSize = 'sm' | 'md' | 'lg' | 'xl';

// Card Layout Types
export type CardLayout = 'vertical' | 'horizontal' | 'form';

// Base Card Props
export interface CardProps extends Omit<ChakraCardProps, 'size'> {
  variant?: CardVariant;
  size?: CardSize;
  layout?: CardLayout;
  className?: string;
}

// Card Header Props
export interface CardHeaderProps extends BoxProps {
  avatar?: {
    src?: string;
    name?: string;
    size?: AvatarProps['size'];
  };
  action?: React.ReactNode;
  className?: string;
}

// Card Title Props
export interface CardTitleProps extends HeadingProps {
  className?: string;
}

// Card Description Props
export interface CardDescriptionProps extends TextProps {
  className?: string;
}

// Card Content Props
export interface CardContentProps extends BoxProps {
  className?: string;
}

// Card Footer Props
export interface CardFooterProps extends BoxProps {
  className?: string;
}

// Card Image Props
export interface CardImageProps extends ImageProps {
  className?: string;
  fallbackSrc?: string;
}

// Card Badge Props
export interface CardBadgeProps extends BadgeProps {
  className?: string;
}

// Size configurations
const sizeConfig = {
  sm: {
    card: { p: 2 },
    header: { p: 3 },
    body: { p: 3 },
    footer: { p: 3 },
    title: { size: 'sm' },
    description: { fontSize: 'xs' },
    avatar: { size: 'sm' as const },
  },
  md: {
    card: { p: 4 },
    header: { p: 4 },
    body: { p: 4 },
    footer: { p: 4 },
    title: { size: 'md' },
    description: { fontSize: 'sm' },
    avatar: { size: 'md' as const },
  },
  lg: {
    card: { p: 6 },
    header: { p: 6 },
    body: { p: 6 },
    footer: { p: 6 },
    title: { size: 'lg' },
    description: { fontSize: 'md' },
    avatar: { size: 'lg' as const },
  },
  xl: {
    card: { p: 8 },
    header: { p: 8 },
    body: { p: 8 },
    footer: { p: 8 },
    title: { size: 'xl' },
    description: { fontSize: 'lg' },
    avatar: { size: 'xl' as const },
  },
};

const sizeToChakraSize: Record<CardSize, ChakraCardProps['size']> = {
  sm: 'sm',
  md: 'md',
  lg: 'lg',
  xl: 'lg',
};

// Main Card Component
const Card = React.forwardRef<HTMLDivElement, CardProps>(
  ({ variant = 'elevated', size = 'md', children, ...props }, ref) => {
    const config = sizeConfig[size];

    return (
      <ChakraCard
        ref={ref}
        variant={variant}
        size={sizeToChakraSize[size]}
        {...config.card}
        {...props}
      >
        {children}
      </ChakraCard>
    );
  }
);
Card.displayName = 'Card';

// Card Header Component
const CardHeader = React.forwardRef<HTMLDivElement, CardHeaderProps>(
  ({ avatar, action, children, className, ...props }, ref) => {
    return (
      <ChakraCardHeader
        ref={ref}
        className={cn('card-header', className)}
        {...props}
      >
        <Flex align='center' gap={3} w='full' minW={0}>
          {avatar && (
            <Avatar
              src={avatar.src}
              name={avatar.name}
              size={avatar.size || 'md'}
            />
          )}
          <VStack align='start' flex='1' spacing={1} minW={0} maxW='100%'>
            {children}
          </VStack>
          {action && (
            <Box ml='auto' flexShrink={0}>
              {action}
            </Box>
          )}
        </Flex>
      </ChakraCardHeader>
    );
  }
);
CardHeader.displayName = 'CardHeader';

// Card Title Component
const CardTitle = React.forwardRef<HTMLHeadingElement, CardTitleProps>(
  ({ className, children, ...props }, ref) => {
    return (
      <Heading
        ref={ref}
        className={cn('card-title', className)}
        size='md'
        {...props}
      >
        {children}
      </Heading>
    );
  }
);
CardTitle.displayName = 'CardTitle';

// Card Description Component
const CardDescription = React.forwardRef<
  HTMLParagraphElement,
  CardDescriptionProps
>(({ className, children, ...props }, ref) => {
  return (
    <Text
      ref={ref}
      className={cn('card-description', className)}
      color='gray.500'
      fontSize='sm'
      {...props}
    >
      {children}
    </Text>
  );
});
CardDescription.displayName = 'CardDescription';

// Card Content Component
const CardContent = React.forwardRef<HTMLDivElement, CardContentProps>(
  ({ className, children, ...props }, ref) => {
    return (
      <CardBody ref={ref} className={cn('card-content', className)} {...props}>
        {children}
      </CardBody>
    );
  }
);
CardContent.displayName = 'CardContent';

// Card Footer Component
const CardFooter = React.forwardRef<HTMLDivElement, CardFooterProps>(
  ({ className, children, ...props }, ref) => {
    return (
      <ChakraCardFooter
        ref={ref}
        className={cn('card-footer', className)}
        {...props}
      >
        {children}
      </ChakraCardFooter>
    );
  }
);
CardFooter.displayName = 'CardFooter';

// Card Image Component
const CardImage = React.forwardRef<HTMLImageElement, CardImageProps>(
  ({ className, fallbackSrc, ...props }, ref) => {
    return (
      <Image
        ref={ref}
        className={cn('card-image', className)}
        fallbackSrc={fallbackSrc || '/placeholder-image.png'}
        objectFit='cover'
        {...props}
      />
    );
  }
);
CardImage.displayName = 'CardImage';

// Card Badge Component
const CardBadge = React.forwardRef<HTMLSpanElement, CardBadgeProps>(
  ({ className, ...props }, ref) => {
    return (
      <Badge ref={ref} className={cn('card-badge', className)} {...props} />
    );
  }
);
CardBadge.displayName = 'CardBadge';

// Horizontal Card Layout Component
export const HorizontalCard = React.forwardRef<HTMLDivElement, CardProps>(
  ({ children, className, ...props }, ref) => {
    return (
      <Card
        ref={ref}
        layout='horizontal'
        className={cn('horizontal-card', className)}
        {...props}
      >
        <Flex direction='row' align='stretch' minH='120px'>
          {children}
        </Flex>
      </Card>
    );
  }
);
HorizontalCard.displayName = 'HorizontalCard';

// Form Card Layout Component
export const FormCard = React.forwardRef<
  HTMLDivElement,
  CardProps & {
    title?: string;
    description?: string;
    submitLabel?: string;
    cancelLabel?: string;
    onSubmit?: () => void;
    onCancel?: () => void;
    isLoading?: boolean;
  }
>(
  (
    {
      title,
      description,
      submitLabel = 'Submit',
      cancelLabel = 'Cancel',
      onSubmit,
      onCancel,
      isLoading = false,
      children,
      className,
      ...props
    },
    ref
  ) => {
    return (
      <Card
        ref={ref}
        layout='form'
        className={cn('form-card', className)}
        {...props}
      >
        {(title || description) && (
          <CardHeader>
            {title && <CardTitle>{title}</CardTitle>}
            {description && <CardDescription>{description}</CardDescription>}
          </CardHeader>
        )}

        <CardContent>
          <VStack spacing={4} align='stretch'>
            {children}
          </VStack>
        </CardContent>

        <CardFooter>
          <ButtonGroup spacing={2} ml='auto'>
            {onCancel && (
              <Button variant='outline' onClick={onCancel}>
                {cancelLabel}
              </Button>
            )}
            {onSubmit && (
              <Button
                colorScheme='blue'
                onClick={onSubmit}
                isLoading={isLoading}
                loadingText='Submitting...'
              >
                {submitLabel}
              </Button>
            )}
          </ButtonGroup>
        </CardFooter>
      </Card>
    );
  }
);
FormCard.displayName = 'FormCard';

// Card with Image Component
export const ImageCard = React.forwardRef<
  HTMLDivElement,
  CardProps & {
    imageSrc: string;
    imageAlt?: string;
    imageHeight?: string | number;
    title?: string;
    description?: string;
    badges?: Array<{ text: string; colorScheme?: ThemingProps['colorScheme'] }>;
    actions?: React.ReactNode;
  }
>(
  (
    {
      imageSrc,
      imageAlt,
      imageHeight = 200,
      title,
      description,
      badges = [],
      actions,
      children,
      className,
      ...props
    },
    ref
  ) => {
    return (
      <Card
        ref={ref}
        className={cn('image-card', className)}
        overflow='hidden'
        {...props}
      >
        <CardImage
          src={imageSrc}
          alt={imageAlt || title || 'Card image'}
          height={imageHeight}
          width='100%'
        />

        <CardHeader>
          {title && <CardTitle>{title}</CardTitle>}
          {description && <CardDescription>{description}</CardDescription>}
          {badges.length > 0 && (
            <HStack spacing={2} mt={2}>
              {badges.map((badge, index) => (
                <CardBadge
                  key={index}
                  colorScheme={badge.colorScheme || 'blue'}
                >
                  {badge.text}
                </CardBadge>
              ))}
            </HStack>
          )}
        </CardHeader>

        {children && <CardContent>{children}</CardContent>}

        {actions && <CardFooter>{actions}</CardFooter>}
      </Card>
    );
  }
);
ImageCard.displayName = 'ImageCard';

// Card with Avatar Component
export const AvatarCard = React.forwardRef<
  HTMLDivElement,
  CardProps & {
    avatarSrc?: string;
    avatarName?: string;
    avatarSize?: AvatarProps['size'];
    title?: string;
    subtitle?: string;
    description?: string;
    action?: React.ReactNode;
  }
>(
  (
    {
      avatarSrc,
      avatarName,
      avatarSize = 'md',
      title,
      subtitle,
      description,
      action,
      children,
      className,
      ...props
    },
    ref
  ) => {
    return (
      <Card ref={ref} className={cn('avatar-card', className)} {...props}>
        <CardHeader
          avatar={{
            src: avatarSrc,
            name: avatarName || title,
            size: avatarSize,
          }}
          action={action}
        >
          {title && <CardTitle>{title}</CardTitle>}
          {subtitle && (
            <Text fontSize='sm' color='gray.600'>
              {subtitle}
            </Text>
          )}
        </CardHeader>

        {description && (
          <CardContent>
            <CardDescription>{description}</CardDescription>
          </CardContent>
        )}

        {children && <CardContent>{children}</CardContent>}
      </Card>
    );
  }
);
AvatarCard.displayName = 'AvatarCard';

export {
  Card,
  CardHeader,
  CardFooter,
  CardTitle,
  CardDescription,
  CardContent,
  CardImage,
  CardBadge,
};
